import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import EventMainReport from '@/model/EventMainReport';
import { EventSectionSaleProductReport } from '@/model/EventSectionSaleProductReport';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#333333',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#1B4880',
    borderBottomStyle: 'solid',
    paddingBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1B4880',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#555555',
    marginTop: 2,
  },
  headerMeta: {
    fontSize: 8,
    color: '#666666',
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1B4880',
    backgroundColor: '#F0F4F8',
    padding: '4 8',
    marginTop: 10,
    marginBottom: 6,
    borderRadius: 2,
  },
  subSectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#444444',
    marginTop: 6,
    marginBottom: 4,
  },
  table: {
    width: '100%',
    marginVertical: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#1B4880',
    color: '#FFFFFF',
    fontWeight: 'bold',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRowEven: {
    backgroundColor: '#F9FBFD',
  },
  colName: { flex: 3 },
  colQty: { flex: 1, textAlign: 'right' },
  colMoney: { flex: 1.5, textAlign: 'right' },
  colStock: { flex: 1.2, textAlign: 'right' },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888888',
  },
});

const formatCurrency = (val: number | undefined | null): string => {
  if (val === undefined || val === null) return 'R$ 0,00';
  return `R$ ${Number(val).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

interface ProductSectionsPDFProps {
  event: EventMainReport;
  sections: EventSectionSaleProductReport[];
}

export const ProductSectionsPDF: React.FC<ProductSectionsPDFProps> = ({ event, sections }) => {
  const totalMoney = sections.reduce((acc, s) => acc + (s.payments?.money || 0), 0);
  const totalDebit = sections.reduce((acc, s) => acc + (s.payments?.debit || 0), 0);
  const totalCredit = sections.reduce((acc, s) => acc + (s.payments?.credit || 0), 0);
  const totalPix = sections.reduce((acc, s) => acc + (s.payments?.pix || 0), 0);
  const totalOverall = sections.reduce(
    (acc, s) =>
      acc +
      (s.summary?.totalSaleValue ??
        (s.payments?.money || 0) +
          (s.payments?.debit || 0) +
          (s.payments?.credit || 0) +
          (s.payments?.pix || 0)),
    0,
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Cabeçalho */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>Relatório de Produtos por Setor</Text>
            <Text style={styles.headerSubtitle}>{event.name || 'Evento sem nome'}</Text>
          </View>
          <View style={styles.headerMeta}>
            <Text>Data Evento: {event.date ? dayjs(event.date).format('DD/MM/YYYY') : 'N/I'}</Text>
            <Text>Emissão: {dayjs().format('DD/MM/YYYY HH:mm')}</Text>
          </View>
        </View>

        {/* Tabela Consolidada de Meios de Pagamento por Setor */}
        <View style={{ marginBottom: 12 }}>
          <Text style={styles.sectionTitle}>Vendas por Meio de Pagamento</Text>
          <View style={styles.table}>
            <View style={styles.tableHeaderRow}>
              <Text style={{ flex: 2.2 }}>Setor</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>Dinheiro</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>Débito</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>Crédito</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>PIX</Text>
              <Text style={{ flex: 1.4, textAlign: 'right' }}>Total Venda</Text>
            </View>

            {sections.map((sec, sIdx) => {
              const secTotal =
                sec.summary?.totalSaleValue ??
                (sec.payments?.money || 0) +
                  (sec.payments?.debit || 0) +
                  (sec.payments?.credit || 0) +
                  (sec.payments?.pix || 0);

              return (
                <View
                  key={`pay-row-${sIdx}`}
                  style={[styles.tableRow, sIdx % 2 === 1 ? styles.tableRowEven : {}]}
                >
                  <Text style={{ flex: 2.2 }}>{sec.name}</Text>
                  <Text style={{ flex: 1.2, textAlign: 'right' }}>
                    {formatCurrency(sec.payments?.money)}
                  </Text>
                  <Text style={{ flex: 1.2, textAlign: 'right' }}>
                    {formatCurrency(sec.payments?.debit)}
                  </Text>
                  <Text style={{ flex: 1.2, textAlign: 'right' }}>
                    {formatCurrency(sec.payments?.credit)}
                  </Text>
                  <Text style={{ flex: 1.2, textAlign: 'right' }}>
                    {formatCurrency(sec.payments?.pix)}
                  </Text>
                  <Text style={{ flex: 1.4, textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(secTotal)}
                  </Text>
                </View>
              );
            })}

            {/* Linha Total Geral */}
            <View style={[styles.tableRow, { backgroundColor: '#EAEFF5', fontWeight: 'bold' }]}>
              <Text style={{ flex: 2.2 }}>TOTAL GERAL</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>{formatCurrency(totalMoney)}</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>{formatCurrency(totalDebit)}</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>{formatCurrency(totalCredit)}</Text>
              <Text style={{ flex: 1.2, textAlign: 'right' }}>{formatCurrency(totalPix)}</Text>
              <Text style={{ flex: 1.4, textAlign: 'right' }}>{formatCurrency(totalOverall)}</Text>
            </View>
          </View>
        </View>

        {sections.map((sec, sIdx) => (
          <View key={`sec-${sIdx}`} style={{ marginBottom: 10 }}>
            <Text style={styles.sectionTitle}>
              Setor: {sec.name} (Vendas: {formatCurrency(sec.summary?.totalSaleValue)})
            </Text>

            {/* Pagamentos no Setor */}
            {sec.payments && (
              <View
                style={{
                  flexDirection: 'row',
                  backgroundColor: '#FAFAFA',
                  borderWidth: 1,
                  borderColor: '#EEEEEE',
                  padding: 4,
                  marginBottom: 6,
                  borderRadius: 2,
                  justifyContent: 'space-between',
                  fontSize: 8,
                }}
              >
                <Text>Dinheiro: {formatCurrency(sec.payments.money)}</Text>
                <Text>Débito: {formatCurrency(sec.payments.debit)}</Text>
                <Text>Crédito: {formatCurrency(sec.payments.credit)}</Text>
                <Text>PIX: {formatCurrency(sec.payments.pix)}</Text>
              </View>
            )}

            {sec.categories?.map((cat, cIdx) => (
              <View key={`cat-${cIdx}`} style={{ marginBottom: 6 }}>
                <Text style={styles.subSectionTitle}>Categoria: {cat.name}</Text>
                <View style={styles.table}>
                  <View style={styles.tableHeaderRow}>
                    <Text style={styles.colName}>Produto</Text>
                    <Text style={styles.colQty}>Vendidos</Text>
                    <Text style={styles.colMoney}>V. Unit</Text>
                    <Text style={styles.colMoney}>Venda</Text>
                    <Text style={styles.colMoney}>Comissão</Text>
                    <Text style={styles.colStock}>Est. Inc.</Text>
                    <Text style={styles.colStock}>Est. Disp.</Text>
                  </View>

                  {cat.products?.map((prod, pIdx) => (
                    <View
                      key={`p-${pIdx}`}
                      style={[styles.tableRow, pIdx % 2 === 1 ? styles.tableRowEven : {}]}
                    >
                      <Text style={styles.colName}>{prod.name}</Text>
                      <Text style={styles.colQty}>{prod.amountSold}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(prod.unitValue)}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(prod.totalSaleValue)}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(prod.totalCommission)}</Text>
                      <Text style={styles.colStock}>{prod.initialInventory || 0}</Text>
                      <Text style={styles.colStock}>{prod.availableStock || 0}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}

            {sec.machines && sec.machines.length > 0 && (
              <View style={{ marginTop: 4 }}>
                <Text style={styles.subSectionTitle}>POS no Setor</Text>
                <View style={styles.table}>
                  <View style={styles.tableHeaderRow}>
                    <Text style={styles.colName}>Serial</Text>
                    <Text style={styles.colQty}>Vendidos</Text>
                    <Text style={styles.colQty}>Cortesias</Text>
                    <Text style={styles.colMoney}>Desconto</Text>
                    <Text style={styles.colMoney}>Venda</Text>
                    <Text style={styles.colMoney}>Comissão</Text>
                  </View>

                  {sec.machines.map((m, mIdx) => (
                    <View
                      key={`m-${mIdx}`}
                      style={[styles.tableRow, mIdx % 2 === 1 ? styles.tableRowEven : {}]}
                    >
                      <Text style={styles.colName}>{m.serialNumber}</Text>
                      <Text style={styles.colQty}>{m.amountSold}</Text>
                      <Text style={styles.colQty}>{m.courtesies}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(m.discount)}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(m.totalSaleValue)}</Text>
                      <Text style={styles.colMoney}>{formatCurrency(m.totalCommission)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        ))}

        {/* Rodapé Fixo */}
        <View style={styles.footer} fixed>
          <Text>Banca do Ingresso - Relatório de Produtos por Setor</Text>
          <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};
